import React from "react";

import styled from "styled-components";

import { VERO1_ACCENT } from "manager/components/vero1/Vero1ReviewPanel/styled";

const Block = styled.div<{ $inset: number; $gap: number }>`
  display: flex;
  flex-direction: column;
  gap: 1px;
  margin-left: ${({ $inset }) => $inset}px;
  padding-left: ${({ $gap }) => $gap}px;
  border-left: 2px solid ${VERO1_ACCENT};
  margin-top: auto;
`;

const NOTE_INK = "#8b87a0";

const NOTE_FONT_PX = { card: 11.5, modal: 14 } as const;

export type Vero1NoteSurface = keyof typeof NOTE_FONT_PX;

const noteFont = (surface: Vero1NoteSurface) =>
  `400 ${NOTE_FONT_PX[surface]}px/1.4 "Work Sans", sans-serif`;

const NoteLine = styled.span<{ $surface: Vero1NoteSurface }>`
  font: ${({ $surface }) => noteFont($surface)};
  color: ${NOTE_INK};
`;

const LinkText = styled.span`
  text-decoration: underline;
  white-space: nowrap;
`;

const Arrow = styled.span`
  display: inline-block;
  vertical-align: middle;
  line-height: 1;
`;

/* !important throughout: the card stylesheet styles bare <button> elements as
   bordered pills, which would make this read as a secondary button rather
   than the text link it is. */
const ReviewLink = styled.button<{ $surface: Vero1NoteSurface }>`
  align-self: flex-start;
  display: inline !important;
  white-space: nowrap !important;
  background: none !important;
  border: none !important;
  border-radius: 0 !important;
  box-shadow: none !important;
  padding: 0 !important;
  margin: 0 !important;
  height: auto !important;
  min-width: 0 !important;
  width: auto !important;
  cursor: pointer;
  font: ${({ $surface }) => noteFont($surface)} !important;
  color: ${NOTE_INK} !important;
  text-decoration: none !important;
  text-align: left;

  &:hover,
  &:focus {
    background: none !important;
    text-decoration: none !important;
  }
`;

export const VERO1_UPDATED_NOTE = "Updated by VERO1";

const SURFACE_METRICS: Record<Vero1NoteSurface, { inset: number; gap: number }> = {
  card: { inset: 12, gap: 8 },
  modal: { inset: 0, gap: 10 },
};

export const Vero1UpdatedNote: React.FC<{
  onReviewChanges: (e: React.MouseEvent) => void;
  testId?: string;
  surface?: Vero1NoteSurface;
}> = ({ onReviewChanges, testId = "vero1-updated-note", surface = "card" }) => {
  const { inset, gap } = SURFACE_METRICS[surface];
  return (
    <Block data-testid={testId} $inset={inset} $gap={gap}>
      <NoteLine $surface={surface}>{VERO1_UPDATED_NOTE}</NoteLine>
      <ReviewLink
        type="button"
        data-testid="vero1-review-changes-link"
        onClick={onReviewChanges}
        $surface={surface}
      >
        <LinkText>Review changes</LinkText>&nbsp;<Arrow>&rarr;</Arrow>
      </ReviewLink>
    </Block>
  );
};
